'use client';
import { pushToAdobeDataLayer } from './adobeDataLayer';
import type { Position } from './positions';
import {
  clickTransforms,
  impressionTransforms as otherTransforms,
  pageLoadTransforms,
} from './transforms';
import { EVENT_NAMES, type IAnchorClickEvent } from './types/base';
import type {
  ClickEventMap,
  IClickInputOverrides,
  IImpressionInputOverrides,
  ImpressionEventMap,
  IPageLoadInputOverrides,
  PageLoadEventMap,
} from './types/registry';

export type { IAnchorClickEvent };

/**
 * Resolves a link's tracked name: uses the given name if provided,
 * otherwise falls back to the click event target's rendered text.
 */
export const resolveLinkName = (
  event: IAnchorClickEvent | undefined,
  name: string | undefined,
): string | undefined =>
  name ?? (event?.currentTarget as HTMLElement | null)?.innerText;

type TransformMap = Record<string, ((input: unknown) => object) | undefined>;

/**
 * Resolves the consumer-facing input type for a click event.
 * If an override exists in ClickInputOverrides, use it;
 * otherwise fall back to the wire payload minus event.
 */
type ClickInput<K extends keyof ClickEventMap> =
  K extends keyof IClickInputOverrides
    ? IClickInputOverrides[K]
    : Omit<ClickEventMap[K], 'event'>;

/**
 * Track a click event. Fully type-safe:
 * - `event` autocompletes to all registered click events
 * - `payload` is narrowed to the exact shape for that event
 */
export const trackClick = <K extends keyof ClickEventMap>(
  eventName: K,
  payload: ClickInput<K>,
): void => {
  const transform = (clickTransforms as TransformMap)[eventName as string];
  const transformedPayload = transform ? transform(payload) : payload;
  pushToAdobeDataLayer({
    ...transformedPayload,
    event: eventName as string,
  });
};

/**
 * Track a click on an anchor element. Extracts link text from the event target
 * automatically; pass `options.name` to override it. If the element has an href,
 * navigation is deferred by `options.followLinkDelay` (default 150 ms) to give
 * the Adobe data layer time to register the event before the page unloads.
 */
export const trackAnchorClick = (
  event: IAnchorClickEvent,
  options?: {
    name?: string;
    followLinkDelay?: number;
    position?: Position | string;
  },
): void => {
  const target = event.currentTarget as HTMLAnchorElement;
  const name = options?.name ?? target.innerText;
  const delay = options?.followLinkDelay ?? 150;
  const { href } = target;

  trackClick(EVENT_NAMES.anchorTagClick, {
    linkDetails: { linkName: name, linkPosition: options?.position },
  });

  if (href) {
    event.preventDefault();
    setTimeout(() => {
      window.location.href = href;
    }, delay);
  }
};

type ImpressionInput<K extends keyof ImpressionEventMap> =
  K extends keyof IImpressionInputOverrides
    ? IImpressionInputOverrides[K]
    : Omit<ImpressionEventMap[K], 'event'>;

export const trackImpression = <K extends keyof ImpressionEventMap>(
  eventName: K,
  payload: ImpressionInput<K>,
): void => {
  const transform = (otherTransforms as TransformMap)[eventName as string];
  const transformedPayload = transform ? transform(payload) : payload;
  pushToAdobeDataLayer({
    ...transformedPayload,
    event: eventName as string,
  });
};

type PageLoadInput<K extends keyof PageLoadEventMap> =
  K extends keyof IPageLoadInputOverrides
    ? IPageLoadInputOverrides[K]
    : Omit<PageLoadEventMap[K], 'event'>;

export const trackPageLoad = <K extends keyof PageLoadEventMap>(
  eventName: K,
  payload: PageLoadInput<K>,
): void => {
  const transform = (pageLoadTransforms as TransformMap)[eventName as string];
  const transformedPayload = transform ? transform(payload) : payload;
  pushToAdobeDataLayer({
    ...transformedPayload,
    event: eventName as string,
  });
};

/**
 * Escape hatch for one-off events not yet in the registry.
 * Logs a warning in development.
 */
export const trackUntyped = (
  event: string,
  payload: Record<string, unknown>,
): void => {
  if (process.env.NODE_ENV === 'development') {
    console.warn(
      `[tagging] Untyped event "${event}" — add it to @kmx/analytics`,
    );
  }
  pushToAdobeDataLayer({ ...payload, event });
};
